import React, { useEffect } from 'react';

import { IReportFilterDtoPlanYearPeriodEnum } from '@/dtos';
import { useFilters } from '@/utils';

import { useFavoriteReportHandler } from '../../hooks';
import { ReportDetailsProps, ReportTab, ReportTypeKeys } from '../../report.types';
import { useGetReportEmployerId, useMultipleEmployerSelectAvailable } from '../hooks';
import ReportDetailsBase from '../ReportDetailsBase';

import {
  DelinquentClaimsReportTableParams,
  DelinquentClaimsReportView,
  DelinquentClaimsSummaryReportParams,
} from './DelinquentClaimsReport.types';
import DelinquentClaimsReportSummary from './DelinquentClaimsReportSummary';
import DelinquentClaimsReportTable from './DelinquentClaimsReportTable';

type FiltersValues = DelinquentClaimsReportTableParams &
  DelinquentClaimsSummaryReportParams & {
    tab: ReportTab;
  };

interface Props extends ReportDetailsProps<FiltersValues> {}

const DelinquentClaimsReportDetails: React.FC<Props> = ({
  defaultEmployerId,
  defaultEmployerIds,
  defaultTab,
  isPreviewMode,
  onChangeFilters,
  defaultFilterValues,
}) => {
  const employerId = useGetReportEmployerId();
  const isMultipleEmployerAvailable = useMultipleEmployerSelectAvailable();

  const { filtersValues, setFilterValue } = useFilters<FiltersValues>(
    {
      tab: defaultTab || ReportTab.Summary,
      employerId: defaultEmployerId || employerId || '',
      employerIds: defaultEmployerIds || employerId ? [employerId] : [],
      searchString: '',
      isSSNShowed: false,
      reportView: DelinquentClaimsReportView.ByEmployee,
      accountTypes: [],
      planIds: [],
      planYearPeriod: [],
      claimStatuses: [],
      ...defaultFilterValues,
    },
    {
      disabled: isPreviewMode,
      autoClear: true,
    }
  );
  useEffect(() => {
    if (isPreviewMode) {
      return;
    }

    setFilterValue.accountTypes([]);
    setFilterValue.planYearPeriod([]);
    setFilterValue.planIds([]);
    if (isMultipleEmployerAvailable) {
      setFilterValue.planYearPeriod([IReportFilterDtoPlanYearPeriodEnum.CURRENT]);
    } else {
      setFilterValue.planYearPeriod([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersValues.employerId, filtersValues.employerIds?.join(',')]);
  useEffect(() => {
    if (isPreviewMode) {
      return;
    }
    if (employerId && !filtersValues.employerId) {
      setFilterValue.employerId(employerId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employerId]);
  useEffect(() => {
    if (isMultipleEmployerAvailable) {
      setFilterValue.planYearPeriod([IReportFilterDtoPlanYearPeriodEnum.CURRENT]);
    } else {
      setFilterValue.planYearPeriod([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMultipleEmployerAvailable]);
  useEffect(() => {
    if (defaultTab) {
      setFilterValue.tab(defaultTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultTab]);

  useEffect(() => {
    if (isPreviewMode && onChangeFilters) {
      onChangeFilters(filtersValues);
    }
  }, [filtersValues, isPreviewMode, onChangeFilters]);

  const { isFavorite, handleToggleReportKey } = useFavoriteReportHandler(ReportTypeKeys.DelinquentClaims);

  return (
    <ReportDetailsBase
      isPreviewMode={isPreviewMode}
      testId="delinquent-claims-report-details"
      isFavorite={isFavorite}
      onToggleFavorite={handleToggleReportKey}
      tab={filtersValues.tab}
      onSetTab={setFilterValue.tab}
      title="Delinquent Claims"
    >
      {filtersValues.tab === ReportTab.Report ? (
        <DelinquentClaimsReportTable
          defaultEmployerId={defaultEmployerId}
          isPreviewMode={isPreviewMode}
          key={filtersValues.tab}
          filtersValues={filtersValues}
          onSetFilterValue={setFilterValue}
        />
      ) : (
        <DelinquentClaimsReportSummary
          defaultEmployerId={defaultEmployerId}
          isPreviewMode={isPreviewMode}
          filtersValues={filtersValues}
          onSetFilterValue={setFilterValue}
        />
      )}
    </ReportDetailsBase>
  );
};

export default DelinquentClaimsReportDetails;
